import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { LuArrowLeft, LuNewspaper } from "react-icons/lu";
import PublicNav, { PublicFooter } from "../components/PublicNav";
import SEO from "../components/SEO";
import { Btn, Card, Badge, Skel, Empty, Note } from "../components/Ui";
import { getPostById, blogPosts as localPosts } from "./blogData";
import "./blog.css";

const BLOG_API = "https://blog-post-response-streaming-v2-980069659423.us-east1.run.app";

function fmtDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/* Legacy content is an array of blocks (paragraph / heading / list).
   If a post ever ships content as an HTML string, sanitize it first. */
function renderContent(content) {
  if (Array.isArray(content)) {
    return content.map((block, index) => {
      if (block?.type === "paragraph") {
        return <p key={index}>{block.content}</p>;
      }
      if (block?.type === "heading") {
        const Tag = `h${block.level || 2}`;
        return <Tag key={index}>{block.content}</Tag>;
      }
      if (block?.type === "list") {
        return (
          <ul key={index}>
            {(block.items || []).map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        );
      }
      return null;
    });
  }
  if (typeof content === "string" && content.trim()) {
    return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />;
  }
  return null;
}

export default function BlogDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const slug = queryParams.get("slug");

  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    /* locally authored article: resolve from the bundle, no network call */
    const localPost = getPostById(slug);
    if (localPost) {
      setPost({
        title: localPost.title,
        seoTitle: localPost.seoTitle,
        slug: localPost.id,
        content: localPost.content,
        excerpt: localPost.description,
        author: localPost.author,
        published_at: localPost.date,
        reading_time: localPost.readTime,
        tag: localPost.category,
        local: true,
      });
      setRelatedPosts(
        localPosts
          .filter((p) => p.id !== localPost.id && p.audience === localPost.audience)
          .slice(0, 3)
          .map((p) => ({
            slug: p.id,
            title: p.title,
            excerpt: p.description,
            published_at: p.date,
            reading_time: p.readTime,
          }))
      );
      setLoading(false);
      setError("");
      return () => {
        active = false;
      };
    }

    const getDetailedPost = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(BLOG_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            operation: "get",
            slug: slug,
          }),
        });
        if (response.ok) {
          const data = await response.json();
          if (active) setPost(data.post || null);
        } else {
          console.error("Error fetching post", await response.text());
          if (active) setError("We could not load this post right now. Please try again in a moment.");
        }
      } catch (err) {
        console.error("Network error:", err);
        if (active) setError("Network error while loading this post. Please check your connection and try again.");
      } finally {
        if (active) setLoading(false);
      }
    };

    const getRelatedPosts = async () => {
      try {
        const response = await fetch(BLOG_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            operation: "get",
            slug: slug,
            include_related: true,
          }),
        });
        if (response.ok) {
          const data = await response.json();
          if (active) setRelatedPosts(Array.isArray(data.related_posts) ? data.related_posts : []);
        } else {
          console.error("Error fetching related posts", await response.text());
        }
      } catch (err) {
        console.error("Network error:", err);
      }
    };

    getDetailedPost();
    getRelatedPosts();
    return () => {
      active = false;
    };
  }, [slug]);

  const openPost = (p) => {
    if (!p?.slug) return;
    navigate(`/blogDetailed?slug=${p.slug}`);
  };

  const onCardKey = (e, p) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openPost(p);
    }
  };

  const created = post ? fmtDate(post.created_at) : "";
  const updated = post ? fmtDate(post.updated_at) : "";

  const postDesc =
    post?.excerpt ||
    post?.meta_description ||
    post?.summary ||
    post?.subtitle ||
    (post ? `${post.title} — career insights from CareerSavvy.` : "");

  const articleJsonLd = post
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        datePublished: post.created_at || undefined,
        dateModified: post.updated_at || post.created_at || undefined,
        author: post.author
          ? { "@type": "Person", name: post.author }
          : { "@type": "Organization", name: "CareerSavvy" },
        image: post.featured_image || "https://careersavvy.ai/og-card.png",
        publisher: {
          "@type": "Organization",
          name: "CareerSavvy",
          logo: { "@type": "ImageObject", url: "https://careersavvy.ai/favicon-v2-512.png" },
        },
        mainEntityOfPage: `https://careersavvy.ai/blogDetailed?slug=${slug}`,
        description: postDesc,
      }
    : null;

  return (
    <div className="csx">
      {post ? (
        <SEO
          title={post.seoTitle || post.title}
          description={postDesc}
          canonicalPath={`/blogDetailed?slug=${encodeURIComponent(slug || "")}`}
          ogType="article"
          ogImage={post.featured_image || undefined}
          jsonLd={articleJsonLd}
          breadcrumbs={[
            { label: "Blog", path: "/blog" },
            { label: post.title, path: `/blogDetailed?slug=${encodeURIComponent(slug || "")}` },
          ]}
        />
      ) : (
        <SEO title="Career insights" description="Career insights and job-search tips from CareerSavvy." canonicalPath="/blog" />
      )}
      <div className="csx-page">
        <PublicNav />

        <main>
          <section className="land-section">
            <div className="wrap">
              <article className="blog-article">
                <div className="mb-3">
                  <Btn variant="quiet" size="sm" to="/blog">
                    <LuArrowLeft size={15} /> Back to all posts
                  </Btn>
                </div>

                {error ? (
                  <Note tone="danger">{error}</Note>
                ) : loading ? (
                  <div className="stack-3" aria-hidden="true">
                    <Skel w={140} h={22} r={999} />
                    <Skel w="88%" h={34} />
                    <Skel w="52%" h={13} />
                    <Skel w="100%" h={14} className="mt-3" />
                    <Skel w="97%" h={14} />
                    <Skel w="93%" h={14} />
                    <Skel w="70%" h={14} />
                  </div>
                ) : !post ? (
                  <Empty
                    icon={<LuNewspaper size={26} />}
                    title="Post not found"
                    sub="This article may have moved or is no longer published."
                    action={<Btn variant="quiet" to="/blog">Back to all posts</Btn>}
                  />
                ) : (
                  <div className="stack-3">
                    {post.tags?.length ? (
                      <div className="row" style={{ flexWrap: "wrap", gap: 8 }}>
                        {post.tags.map((tag) => (
                          <Badge key={tag} tone="accent">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    ) : null}

                    <h1 className="blog-article-title">{post.title}</h1>

                    <div className="meta-row t-xs">
                      {post.author ? <span>{post.author}</span> : null}
                      {created ? <span className="meta-dot">Published {created}</span> : null}
                      {updated && updated !== created ? <span className="meta-dot">Updated {updated}</span> : null}
                    </div>

                    {post.featured_image ? (
                      <img src={post.featured_image} alt={post.title || "Article cover"} className="blog-cover" />
                    ) : null}

                    <div className="prose">{renderContent(post.content)}</div>
                  </div>
                )}
              </article>

              {/* Related posts (legacy sidebar, now below the article) */}
              {!loading && !error && relatedPosts.length > 0 ? (
                <div className="blog-article mt-4">
                  <div className="mb-2">
                    <span className="t-label">Related posts</span>
                  </div>
                  <div className="grid-2">
                    {relatedPosts.map((p) => (
                      <Card
                        key={p.id || p.slug}
                        hover
                        className="blog-item"
                        role="link"
                        tabIndex={0}
                        onClick={() => openPost(p)}
                        onKeyDown={(e) => onCardKey(e, p)}
                      >
                        <div className="stack-2">
                          {p.tags?.[0] ? <span className="t-label">{p.tags[0]}</span> : null}
                          <h3 className="t-h3 blog-clamp-2">{p.title}</h3>
                          <div className="meta-row t-xs">
                            {p.author ? <span>{p.author}</span> : null}
                            <span className="meta-dot">{fmtDate(p.created_at)}</span>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        </main>

        <PublicFooter />
      </div>
    </div>
  );
}
